import { appstr } from "@coreapp/utils/appstr";
import { GetWSMgr } from "@/Utils/StateLib";
import { Flex, Text } from "@theorgchartdev/storybook";
import { FormatFieldValue } from "@coreapp/utils/FormatFieldValue";
import { FormatPositionLabel } from "@coreapp/utils/FormatPositionLabel";
import { OverrideFactory } from "@coreapp/CoreApp/OverrideFactory";
import type { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";

export const promptToClosePosition = async (dataObjs: QObjDBObject[]) => {
  const posHolderMap: Record<string, { data: QObjDBObject; holders: QObjDBObject[] }> = {};
  for (const obj of dataObjs) {
    if (!obj.IsPositionObj()) {
      continue;
    }
    const holders = obj.GetHolderList() ?? [];
    const positionID = `${obj.GetObjectRecordID()}`;
    posHolderMap[positionID] = {
      data: obj,
      holders
    };
  }

  const posHoldersData: { data: QObjDBObject; holders: QObjDBObject[] }[] =
    Object.values(posHolderMap);

  if (posHoldersData.length === 1) {
    const posDetails = posHoldersData[0];
    const isUnassignedPosition = posDetails.holders.length === 0;
    if (isUnassignedPosition) {
      await OverrideFactory.ClosePosition([posDetails.data], false);
      return;
    }
  }

  // Only show positions with holders in the confirmation dialog
  const nonEmptyPosData = Object.entries(posHolderMap).filter(
    ([, { holders }]) => holders.length > 0
  );
  if (nonEmptyPosData.length === 0) {
    // All positions are unassigned, so just close them directly
    await OverrideFactory.ClosePosition(dataObjs, false);
    return;
  }

  const content = (
    <Flex direction="column">
      <Text>
        {appstr("Are you sure you want to close the following position?", {
          __position__: appstr(
            posHoldersData.length === 1 ? "Position" : "Positions"
          ).toLocaleLowerCase()
        })}
      </Text>
      <ul className="oc-list-disc">
        {nonEmptyPosData.map(([posID, { data, holders }]) => {
          return (
            <li key={posID}>
              <Text modifier="bold">{FormatPositionLabel(data)}</Text>
              {holders.length > 0 ? (
                <ul className="oc-list-disc">
                  {holders.map(holder => {
                    const recordId = holder.GetObjectRecordID();
                    const value = holder.SmartGetFieldValue("Name");
                    const nameFieldDef = holder.GetFieldDefByID("Name");

                    return (
                      <li key={`${recordId}`}>
                        <Text>{FormatFieldValue(value, nameFieldDef)}</Text>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Flex>
  );

  const wsMgr = GetWSMgr();
  wsMgr?.CreateReactAlert(
    appstr("Close position"),
    content,
    async () => {
      await OverrideFactory.ClosePosition(dataObjs, false);
    },
    async () => {
      await OverrideFactory.ClosePosition(dataObjs, true);
    },
    appstr("Close and unassign"),
    appstr("Close and release"),
    () => {
      /* Dismiss */
    },
    {
      secondaryBtnProps: {
        modifier: "danger-secondary"
      },
      primaryBtnProps: {
        modifier: "danger"
      },
      alertProps: {
        severity: "info",
        message: appstr(
          "Closed positions remain in your chart and table view, but can be filtered out"
        )
      }
    },
    "alert-close-position",
    appstr("Cancel"),
    () => {}
  );
};
